import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import {
  Card,
  Container,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
} from '@mui/material';
import { LoadingUI } from '../../utils/LayoutStyles';
import { CommonLabel } from '../../utils/Common';
import API from '../../api';

export default function BankTransfersDetail() {
  const { state } = useLocation();
  const transferID = state.transferID;
  const transferName = state.transferName;

  const [transfer, setTransfer] = useState({});
  const [cards, setCards] = useState([]);
  const [unpaidCards, setUnpaidCards] = useState([]);
  const [mergedCards, setMergedCards] = useState([]);
  const [cardLoading, setCardLoading] = useState(true);
  const [unpaidLoading, setUnpaidLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [bankPaymentInfo, setBankPaymentInfo] = useState({});
  const [bankInfoLoading, setBankInfoLoading] = useState(true);
  const [totalCost, setTotalCost] = useState(0);
  const [currency, setCurrency] = useState('');

  useEffect(() => {
    getBankCumulativeCards();
    getUnpaidCards();
    getBankPaymentInfo();
  }, []);

  useEffect(() => {
    if (!cardLoading && !unpaidLoading) {
      setMergedCards([...cards, { separator: true }, ...unpaidCards]);

      setTimeout(() => {
        setIsLoading(false);
      }, 300);
    }
  }, [cardLoading, unpaidLoading, cards, unpaidCards]);

  const getBankCumulativeCards = () => {
    API.get(`/bankcumulative/${transferID}?expand=cards`)
      .then((response) => {
        setTransfer(response.data);
        setCards(response.data.cards);
        setCurrency(response.data.currency);
        setCardLoading(false);
      })
      .catch((error) => {
        setCardLoading(false);
      });
  };

  const getUnpaidCards = () => {
    API.get(`/bankcumulative/cards`)
      .then((response) => {
        setUnpaidCards(response.data.cards);
        setUnpaidLoading(false);
      })
      .catch((error) => {
        setUnpaidLoading(false);
      });
  };

  const handleToggleCard = (card) => {
    setIsLoading(true);

    if (cards.some((arrayCard) => arrayCard.id, card.id)) {
      API.post(`/bankcumulative/${transferID}/deletecards?membership_card=${card.id}`).then((response) => {
        setMergedCards([]);
        getBankCumulativeCards();
        getUnpaidCards();
      });
    } else {
      API.post(`/bankcumulative/${transferID}/cards?membership_card=${card.id}`).then((response) => {
        setMergedCards([]);
        getBankCumulativeCards();
        getUnpaidCards();
      });
    }
  };

  const getBankPaymentInfo = () => {
    API.get('/paymentbank').then((response) => {
      setBankPaymentInfo(response.data[0]);
      setBankInfoLoading(false);
    });
  };

  useEffect(() => {
    let total = 0;
    if (cards && cards.length > 0) {
      total = cards.reduce((acc, card) => {
        if (typeof card.total_amount === 'number') {
          return acc + card.total_amount;
        }

        return acc;
      }, 0);
    }
    setTotalCost(total);
  }, [cards]);

  // useEffect(() => {
  //   const costSum = selectedCards.reduce((sum, card) => sum + parseFloat(card.cost), 0);
  //   setTotalCost(costSum);
  // }, [selectedCards]);

  return (
    <>
      <Helmet>
        <title>Bank Transfers</title>
      </Helmet>

      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography
                sx={{
                  height: '24px',
                  width: 'fit-content',
                  whiteSpace: 'nowrap',
                  borderRadius: '6px',
                  padding: '0 10px',
                  textAlign: 'center',
                  fontWeight: '700',
                  fontSize: '1.1em',
                }}
              >
                {transferName}
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ paddingTop: '24px' }}>
          <Grid item xs={12}>
            <Card sx={{ padding: '24px' }}>
              {bankInfoLoading ? (
                LoadingUI()
              ) : (
                <Stack direction="row" alignItems="end" justifyContent="space-between">
                  <Stack direction="column" gap="8px">
                    <Stack direction="row" gap="8px">
                      <Typography sx={{ fontWeight: 'bold' }}>Stato:</Typography>
                      <CommonLabel textColor={'#118d57'} bgColor={'#d3efdf'} text={'Eseguito'} />
                    </Stack>
                    <Stack direction="row" gap="8px">
                      <Typography sx={{ fontWeight: 'bold' }}>Beneficiario:</Typography>
                      <Typography>{bankPaymentInfo.intestatario}</Typography>
                    </Stack>
                    <Stack direction="row" gap="8px">
                      <Typography sx={{ fontWeight: 'bold' }}>IBAN:</Typography>
                      <Typography>{bankPaymentInfo.iban}</Typography>
                    </Stack>
                    <Stack direction="row" gap="8px">
                      <Typography sx={{ fontWeight: 'bold' }}>Causale:</Typography>
                      <Typography>{bankPaymentInfo.causale}</Typography>
                    </Stack>
                    <Stack direction="row" gap="8px">
                      <Typography sx={{ fontWeight: 'bold' }}>Importo:</Typography>
                      <Typography>{`${totalCost}€`}</Typography>
                    </Stack>
                  </Stack>
                </Stack>
              )}
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ padding: '24px' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>N° Tessera</TableCell>
                      <TableCell>Nome e Cognome</TableCell>
                      <TableCell>Data Creazione</TableCell>
                      <TableCell>Data Scadenza</TableCell>
                      <TableCell>Costo</TableCell>
                      <TableCell>Valuta</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  {isLoading ? (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                          {LoadingUI()}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  ) : (
                    <TableBody>
                      {mergedCards.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: 'center' }}>
                            Nessuna tessera disponibile
                          </TableCell>
                        </TableRow>
                      ) : (
                        mergedCards.map((card, index) =>
                          card.separator ? (
                            <TableRow key={`separator-${index}`}>
                              <TableCell
                                colSpan={7}
                                sx={{ textAlign: 'center', backgroundColor: '#f4f6f8', padding: '0' }}
                              >
                                Tessere in attesa di pagamento
                              </TableCell>
                            </TableRow>
                          ) : (
                            <TableRow hover key={`${card.id}${index}`}>
                              <TableCell>
                                {card.prefix}
                                {card.card_number}
                              </TableCell>
                              <TableCell>
                                {card.user.name} {card.user.surname}
                              </TableCell>
                              <TableCell>{dayjs(card.created_at).format('DD/MM/YYYY')}</TableCell>
                              <TableCell>{dayjs(card.campaign.end_date).format('DD/MM/YYYY')}</TableCell>
                              <TableCell>{Number(card.total_amount).toFixed(2)}</TableCell>
                              <TableCell>{currency}</TableCell>

                              <TableCell>
                                <Checkbox
                                  checked={cards.some((selectedCard) => selectedCard.id === card.id)}
                                  onChange={() => handleToggleCard(card)}
                                />
                              </TableCell>
                            </TableRow>
                          )
                        )
                      )}
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
